import {
    getEffectivePermissions,
    getWorkspaceRole,
    hasWorkspaceWideProjectVisibility,
    isWorkspaceReadOnlyRole,
} from "../../system/workspace-permissions";

const PROJECT_ROLES = new Set(["manager", "editor", "viewer"]);

export function projectAccessRolesKey(userRoles) {
    return JSON.stringify(
        (Array.isArray(userRoles) ? userRoles : [])
            .map((role) => ({
                id: role?.documentId ?? role?.id ?? null,
                role: role?.role ?? null,
                scopeType: role?.scope_type ?? null,
                scopeId: role?.scope_id ?? null,
                permissions: Array.isArray(role?.permissions) ? [...role.permissions].sort() : [],
            }))
            .sort((left, right) => String(left.id).localeCompare(String(right.id))),
    );
}

export function identityCandidates(value) {
    if (value === null || value === undefined || value === "") return [];
    if (typeof value !== "object") return [String(value)];
    return [value.documentId, value.id]
        .filter((item) => item !== null && item !== undefined && item !== "")
        .map(String);
}

export function sameProjectAccessIdentity(left, right) {
    const rightIdentities = new Set(identityCandidates(right));
    return identityCandidates(left).some((identity) => rightIdentities.has(identity));
}

function roleMatchesAccessContext(role, workspace, membership) {
    if (!role || role.scope_type !== "project" || !role.scope_id) return false;
    if (!PROJECT_ROLES.has(role.role)) return false;
    if (role.workspace && !sameProjectAccessIdentity(role.workspace, workspace)) return false;
    if (
        role.workspace_membership
        && !sameProjectAccessIdentity(role.workspace_membership, membership)
    ) {
        return false;
    }
    return true;
}

export function getProjectScopeRole(userRoles, project, workspace, membership) {
    const projectDocumentId = project?.documentId;
    if (!projectDocumentId) return null;

    return (Array.isArray(userRoles) ? userRoles : []).find((role) => (
        roleMatchesAccessContext(role, workspace, membership)
        && String(role.scope_id) === String(projectDocumentId)
    )) || null;
}

export function getProjectRoleScopeIds(userRoles, workspace, membership) {
    return [...new Set(
        (Array.isArray(userRoles) ? userRoles : [])
            .filter((role) => roleMatchesAccessContext(role, workspace, membership))
            .map((role) => String(role.scope_id).trim())
            .filter(Boolean),
    )];
}

export function hasWorkspaceWideProjectAccess(userRoles) {
    return hasWorkspaceWideProjectVisibility(userRoles);
}

export function filterProjectTabsByAccess(tabs, projectAccess) {
    return (Array.isArray(tabs) ? tabs : []).filter((tab) => (
        !tab?.permission || projectAccess?.can?.(tab.permission)
    ));
}

function isDirectlyRelatedToProject(project, membership) {
    return (Array.isArray(project?.members) && project.members.some((member) => (
        sameProjectAccessIdentity(member, membership)
    )))
        || sameProjectAccessIdentity(project?.owner, membership);
}

export function resolveProjectAccess({
    workspace,
    workspaceStatus,
    activeMembership,
    userRoles,
    project,
}) {
    const roles = Array.isArray(userRoles) ? userRoles : activeMembership?.user_roles || [];
    const workspaceRole = getWorkspaceRole(roles);
    const effectiveProjectRole = getProjectScopeRole(
        roles,
        project,
        workspace,
        activeMembership,
    );
    const operationalStatus = workspaceStatus || workspace?.workspaceStatus || "active";
    const isReadOnly = operationalStatus !== "active"
        || isWorkspaceReadOnlyRole(workspaceRole);
    const isWorkspaceMatch = sameProjectAccessIdentity(project?.workspace, workspace);
    const canViewProject = Boolean(
        project
        && project.archivedAt === null
        && isWorkspaceMatch
        && workspaceRole?.role
        && workspaceRole.role !== "guest"
        && (
            hasWorkspaceWideProjectAccess(roles)
            || isDirectlyRelatedToProject(project, activeMembership)
            || effectiveProjectRole
        )
    );
    const permissions = canViewProject && !isReadOnly
        ? getEffectivePermissions(
            roles,
            project?.documentId,
            project?.rolesConfig,
            operationalStatus,
        )
        : [];
    const can = (permission) => (
        canViewProject
        && !isReadOnly
        && (!permission || permissions.includes("*") || permissions.includes(permission))
    );
    const canManageMembers = can("manage-project-members")
        || can("modify-project-settings");

    return {
        can,
        canViewProject,
        canManageProject: can("modify-project-settings") || canManageMembers,
        canEditProject: can("modify-project-settings"),
        canManageMembers,
        canManageProjectRoles: canManageMembers,
        canCreateTask: can("create-task"),
        canUpdateTask: can("edit-task"),
        canChangeTaskStatus: can("change-task-status") || can("edit-task"),
        canUploadFile: can("upload-file"),
        canImportTasks: can("upload-file") && can("create-task"),
        canShareProject: can("share-project"),
        canArchiveProject: can("archive-project"),
        isReadOnly,
        effectiveProjectRole,
        workspaceRole,
    };
}
